import { Component, inject, Input, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { OfferApiService } from '@app/areas/offers/services/offer.api.service';
import { AsyncPipe, NgIf } from '@angular/common';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { ApplicationProfileService } from '@shared/services/application-profile.service';
import { EpTableComponent, EPTableConfig } from '@ep/shared';
import { Offer } from '@app/resources/services';
import { getOfferStatusSeverity, mapOfferStatusToValue, sortByStartDate } from '@app/areas/offers/utils';
import { AudienceFilterType } from '@app/areas/offers/forms/offer-audience-form/offer-audience-filter.types';
import { OfferStatusTypes } from '@shared/enums';
import { TimezoneService } from '@app/resources/services/timezone.service';
import moment from 'moment-timezone';
import { PortalTheme } from '@app/@shared/models';
import { CssValidatorService } from '@app/@shared/services/css-validator.service';

@Component({
  selector: 'app-offer-table',
  templateUrl: './offer-table.component.html',
  styleUrls: ['./offer-table.component.scss'],
  standalone: true,
  imports: [AsyncPipe, EpTableComponent, NgIf],
})
export class OfferTableComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly offerApiService = inject(OfferApiService);
  private readonly timezoneService = inject(TimezoneService);
  private readonly applicationProfileService = inject(ApplicationProfileService);
  private readonly cssValidatorService = inject(CssValidatorService);

  @Input() public theme: PortalTheme | null = null;

  protected config!: EPTableConfig<Offer>;

  ngOnInit(): void {
    this.initializeTableConfig();
    this.setCSSCustomProperties();
  }

  private initializeTableConfig(): void {
    this.config = {
      header: 'offers',
      pagination: {
        rows: 10,
        showCurrentPageReport: true,
      },
      columns: [
        {
          header: 'Name',
          field: 'namePrivate',
          columnType: 'string',
          defaultValue: 'Missing Name',
        },
        {
          header: 'Start Date',
          field: 'startDateTime',
          columnType: 'string',
          defaultValue: 'Ongoing',
        },
        {
          header: 'Expiration Date',
          field: 'endDateTime',
          columnType: 'string',
          defaultValue: 'Ongoing',
        },
        {
          header: 'Text Schedule',
          field: 'sendTime',
          columnType: 'string',
          defaultValue: 'No text sent',
        },
        {
          header: 'Audience',
          field: 'audienceFilter',
          columnType: 'string',
          defaultValue: 'None',
        },
        {
          header: 'Status',
          field: 'statusType',
          columnType: 'categorical',
          defaultValue: 'N/A',
          fieldToValueMap: mapOfferStatusToValue,
          severity: getOfferStatusSeverity,
        },
      ],
      style: {
        'min-width': '50rem',
        border: '5px',
        'custom-header-background': this.theme?.table.header.backgroundColor || '',
        'custom-header-text-color': this.theme?.table.header.textColor || '',
        'custom-status-primary-background': this.theme?.table.statusColors.primary || '',
        'custom-status-secondary-background': this.theme?.table.statusColors.secondary || '',
        'custom-status-danger-background': this.theme?.table.statusColors.danger || '',
        'custom-table-subheader-background': this.theme?.table.subHeaderBackgroundColor || '',
        'custom-table-subheader-text-color': this.theme?.table.subHeaderTextColor || '',
        'custom-table-body-primary-background': this.theme?.table.body.primaryBackgroundColor || '',
        'custom-table-body-secondary-background': this.theme?.table.body.secondaryBackgroundColor || '',
        'custom-table-body-text-color': this.theme?.table.body.textColor || '',
      },
      globalFilterFields: ['name'],
      actions: {
        addNew: () => {
          this.router.navigate(['new'], { relativeTo: this.route });
        },
        rowClick: (offer: Offer) => {
          this.router.navigate([`${offer.offerId}`], { relativeTo: this.route });
        },
      },
    };
  }

  protected offers$: Observable<Offer[]> = this.applicationProfileService.applicationProfile$.pipe(
    switchMap((appProfile) => {
      const location = appProfile.Location;
      return this.offerApiService.getOffers(location.LocationId);
    }),
    map((offers: Offer[] | null) => {
      if (offers) {
        return [
          ...offers.filter((o) => o.statusType === OfferStatusTypes.Active).sort(sortByStartDate),
          ...offers
            .filter(
              (o) =>
                o.statusType === OfferStatusTypes.Pending ||
                o.statusType === OfferStatusTypes.PendingNoText ||
                o.statusType === OfferStatusTypes.PendingApproval
            )
            .sort(sortByStartDate),
          ...offers
            .filter(
              (o) =>
                o.statusType !== OfferStatusTypes.Active &&
                o.statusType !== OfferStatusTypes.Pending &&
                o.statusType !== OfferStatusTypes.PendingNoText &&
                o.statusType !== OfferStatusTypes.PendingApproval
            )
            .sort(sortByStartDate),
        ].map((offer) => {
          return {
            ...offer,
            startDateTime: this.timezoneService
              .moment(offer.startDateTime)
              .add(this.timezoneService.getClientOffset(), 'hours')
              .subtract(this.timezoneService.getDSTTime(offer.startDateTime), 'hours')
              .format('MM/DD/YYYY, h:mm a'),
            endDateTime: this.timezoneService
              .moment(offer.endDateTime)
              .add(this.timezoneService.getClientOffset(), 'hours')
              .subtract(this.timezoneService.getDSTTime(offer.startDateTime), 'hours')
              .format('MM/DD/YYYY, h:mm a'),
            sendTime: offer.sendTime
              ? this.timezoneService
                  .moment(offer.sendTime)
                  .subtract(this.timezoneService.getDSTTime(offer.sendTime), 'hours')
                  .format('MM/DD/YYYY, h:mm a')
              : null,
            audienceFilter:
              offer.audienceFilter?.filterType === AudienceFilterType.AllConsumers
                ? 'All Consumers'
                : offer.audienceFilter?.filterType === AudienceFilterType.TopConsumers
                ? 'Top Consumers'
                : null,
          } as unknown as Offer;
        });
      }
      return [];
    }),
    shareReplay(1)
  );

  getDSTTime(date: moment.MomentInput) {
    return this.timezoneService.isDST(date) ? 0 : 1;
  }

  private setCSSCustomProperties(): void {
    if (this.theme) {
      // Set CSS custom properties
      document.documentElement.style.setProperty(
        '--custom-status-primary-background',
        this.theme?.table.statusColors.primary || ''
      );
      document.documentElement.style.setProperty(
        '--custom-status-secondary-background',
        this.theme?.table.statusColors.secondary || ''
      );
      document.documentElement.style.setProperty(
        '--custom-status-danger-background',
        this.theme?.table.statusColors.danger || ''
      );
      document.documentElement.style.setProperty(
        '--custom-header-background',
        this.theme?.table.header.backgroundColor
      );
      document.documentElement.style.setProperty(
        '--custom-table-subheader-background',
        this.theme?.table.subHeaderBackgroundColor || ''
      );
      document.documentElement.style.setProperty(
        '--custom-table-subheader-text-color',
        this.theme?.table.subHeaderTextColor || ''
      );
      document.documentElement.style.setProperty(
        '--custom-header-text-color',
        this.theme?.table.header.textColor || ''
      );
      document.documentElement.style.setProperty(
        '--custom-table-body-primary-background',
        this.theme?.table.body.primaryBackgroundColor || ''
      );
      document.documentElement.style.setProperty(
        '--custom-table-body-secondary-background',
        this.theme?.table.body.secondaryBackgroundColor || ''
      );
      document.documentElement.style.setProperty(
        '--custom-table-body-text-color',
        this.theme?.table.body.textColor || ''
      );

      // Check for undefined CSS variables
      this.cssValidatorService.checkUndefinedCssVariables('OfferTableComponent');
    }
  }
}
